import { useTheme } from "@mui/material";
import { flexRender, Row, Table } from "@tanstack/react-table";
import { CSSProperties, Key } from "react";
import { Theme } from "../../Theme";
import { StyledCell, StyledRow } from "../table.styles";
import { DefaultRow } from "../types";
import { ExpandButton } from "./ExpandButton";

type Props<TRow> = {
  row: Row<TRow>;
  rows?: Row<TRow>[];
  className?: string;
  columnsSize: number[];
  style?: CSSProperties;
  table: Table<TRow>;
  onRowClick?: (
    rowId: Key,
    isExpanded: boolean,
    isSelected: boolean,
    row: TRow
  ) => void;
  onVirtualizerMeasure?: () => void;
  shouldExpandOnRowClick: boolean;
};

const RowComponent = <TRow extends DefaultRow>({
  row,
  rows,
  className = "",
  columnsSize,
  style,
  onRowClick,
  table,
  onVirtualizerMeasure,
  shouldExpandOnRowClick,
}: Props<TRow>) => {
  const theme = useTheme<Theme>();
  const rowCanExpand = row.getCanExpand();
  const onExpand = () => {
    const hasSubRows = rows?.some((r) => r.subRows.length > 0);
    if (hasSubRows) {
      rows?.forEach((r) => {
        if (r.id !== row.id && r.getIsExpanded()) {
          r.toggleExpanded();
        }
      });
    }

    row.toggleExpanded();
  };

  return (
    <StyledRow
      theme={theme}
      columnsSize={columnsSize}
      className={`table-row ${
        row.parentId ? "table-row-children" : ""
      } ${className}`}
      key={row.id}
      style={style}
      isClickable={row.getCanExpand() || !!onRowClick}
      isActive={row.getIsExpanded() || row.getIsSelected()}
      isChildRow={!!row.parentId}
      isSomeExpanded={rows?.some((r) => r.getIsExpanded())}
      onClick={() => {
        onRowClick?.(
          row.id,
          row.getIsExpanded(),
          row.getIsSelected(),
          row.original
        );

        onVirtualizerMeasure?.();
        if (shouldExpandOnRowClick && rowCanExpand) {
          onExpand();
        }
      }}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <StyledCell key={cell.id} className={`table-cell  ${cell.column.id}`}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </StyledCell>
        );
      })}
      {table.getCanSomeRowsExpand() ? (
        rowCanExpand ? (
          <ExpandButton isExpanded={row.getIsExpanded()} onExpand={onExpand} />
        ) : (
          <StyledCell style={{ minWidth: "36px", boxSizing: "border-box" }} />
        )
      ) : (
        ""
      )}
    </StyledRow>
  );
};

export default RowComponent;
